import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ThumbsUp, MessageCircle, Send, Star, Award, Loader2, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import LikeButton from '../interactions/LikeButton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Generate a unique visitor ID for tracking reactions
const getVisitorId = () => {
  let visitorId = localStorage.getItem('pharmacy_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('pharmacy_visitor_id', visitorId);
  }
  return visitorId;
};

const AgentHonorSection = ({ language = 'fr' }) => {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentName, setCommentName] = useState('');
  const [commentContent, setCommentContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [userReactions, setUserReactions] = useState({});

  const visitorId = getVisitorId();

  useEffect(() => {
    fetchAgent();
  }, []);

  const fetchAgent = async () => {
    try {
      const response = await axios.get(`${API_URL}/community/agent-honor`);
      setAgent(response.data);
    } catch (error) {
      console.error('Error fetching agent:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReaction = async (reactionType) => {
    if (!agent) return;

    try {
      const response = await axios.post(
        `${API_URL}/community/agent-honor/${agent.id}/react`,
        { reaction_type: reactionType, visitor_id: visitorId }
      );

      // Update local state
      setUserReactions(prev => ({
        ...prev,
        [reactionType]: response.data.action === 'added'
      }));

      // Refetch to get updated counts
      fetchAgent();
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentName.trim() || !commentContent.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/community/agent-honor/${agent.id}/comment`,
        {
          author_name: commentName,
          content: commentContent,
          visitor_id: visitorId
        }
      );
      toast.success('Commentaire envoyé ! Il sera publié après modération.');
      setCommentName('');
      setCommentContent('');
      setShowCommentForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2E7D32]" />
      </div>
    );
  }

  if (!agent) {
    return null;
  }

  const reactions = agent.reactions || {};
  const reactionButtons = [
    { type: 'heart', icon: Heart, label: '❤️', color: 'text-red-500' },
    { type: 'thumbsup', icon: ThumbsUp, label: '👍', color: 'text-blue-500' },
  ];

  return (
    <section className="py-12 bg-gradient-to-br from-amber-50 via-white to-emerald-50">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Award className="w-4 h-4" />
            {language === 'fr' ? 'À l\'honneur' : 'In the Spotlight'}
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {agent.period_type === 'quarter' 
              ? (language === 'fr' ? 'Agent du Trimestre' : 'Agent of the Quarter')
              : (language === 'fr' ? 'Agent du Mois' : 'Agent of the Month')}
          </h2>
          <p className="text-gray-600 text-sm">{agent.period_label}</p>
        </motion.div>

        {/* Agent Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
        >
          <div className="md:flex">
            {/* Photo */}
            <div className="md:w-1/3 relative">
              {agent.photo_url ? (
                <img
                  src={agent.photo_url}
                  alt={agent.name}
                  className="w-full h-64 md:h-full object-cover"
                />
              ) : (
                <div className="w-full h-64 md:h-full bg-gradient-to-br from-[#2E7D32] to-[#1B5E20] flex items-center justify-center">
                  <User className="w-24 h-24 text-white/50" />
                </div>
              )}
              {/* Badge */}
              <div className="absolute top-4 left-4">
                <div className="bg-amber-500 text-white px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1 shadow-lg">
                  <Star className="w-4 h-4 fill-white" />
                  {agent.period_type === 'quarter' ? 'Trimestre' : 'Mois'}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="md:w-2/3 p-6 md:p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{agent.name}</h3>
              <p className="text-[#2E7D32] font-medium mb-4">{agent.role}</p>
              <p className="text-gray-600 leading-relaxed mb-6">{agent.description}</p>

              {/* Reactions */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                {/* Unified Like Button */}
                <LikeButton
                  targetType="agent_honor"
                  targetId={agent.id}
                  size="default"
                  showNames={true}
                  language={language}
                />
                
                {reactionButtons.map((btn) => (
                  <button
                    key={btn.type}
                    onClick={() => handleReaction(btn.type)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all ${
                      userReactions[btn.type]
                        ? 'border-[#2E7D32] bg-[#E8F5E9]'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <span className="text-lg">{btn.label}</span>
                    <span className="font-semibold text-gray-700">
                      {reactions[btn.type] || 0}
                    </span>
                  </button>
                ))}
                <button
                  onClick={() => setShowCommentForm(!showCommentForm)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border-2 border-gray-200 hover:border-[#2E7D32] hover:bg-[#E8F5E9] transition-all bg-white"
                >
                  <MessageCircle className="w-5 h-5 text-gray-500" />
                  <span className="font-semibold text-gray-700">
                    {agent.comment_count || 0}
                  </span>
                </button>
              </div>

              {/* Comment Form */}
              <AnimatePresence>
                {showCommentForm && (
                  <motion.form
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    onSubmit={handleSubmitComment}
                    className="border-t border-gray-100 pt-4 space-y-3"
                  >
                    <Input
                      placeholder="Votre nom"
                      value={commentName}
                      onChange={(e) => setCommentName(e.target.value)}
                      maxLength={50}
                    />
                    <Textarea
                      placeholder="Félicitez cet agent..."
                      value={commentContent}
                      onChange={(e) => setCommentContent(e.target.value)}
                      rows={2}
                      maxLength={500}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setShowCommentForm(false)}
                      >
                        Annuler
                      </Button>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="bg-[#2E7D32] hover:bg-[#1B5E20]"
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Envoyer
                      </Button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Comments */}
          {agent.comments && agent.comments.length > 0 && (
            <div className="border-t border-gray-100 p-6 bg-gray-50">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Commentaires ({agent.comments.length})
              </h4>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {agent.comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="bg-white p-3 rounded-lg border border-gray-100"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 bg-[#E8F5E9] rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-[#2E7D32]">
                          {comment.author_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="font-medium text-gray-900">{comment.author_name}</span>
                    </div>
                    <p className="text-gray-600 text-sm pl-10">{comment.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default AgentHonorSection;
